import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "../../../../../utils/helpers/errors";
import useApi from "../../../../../utils/api";
import { toast } from "react-toastify";

export const useTimeOffVersionsApi = () => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const { authGet, authPost, authPut, authDelete } = useApi();

    const getVersions = useCallback(
        async (id, controller) => {
            setLoading(true);
            try {
                const response = await authGet(`/time-off/versions/${id}`, {
                    signal: controller?.signal
                });
                if(response) {
                    setData(response?.result);
                    setTotalItems(response?.totalItems);
                }
            } catch (error) {
                getErrorMessage(error, t);
            } finally {
                setLoading(false);
            }
        },
        [authGet, t]
    );

    const getLatestVersion = useCallback(async (entityId, params, controller) => {
        setLoading(true);
        try {
            const response = await authGet(`/time-off/versions/${entityId}/latest`, {
                params,
                signal: controller?.signal,
            });
            if(response) setData(response);
        } catch(err) {
            getErrorMessage(err, t);
        } finally {
            setLoading(false);
        }
    }, [authGet, t])

    const createVersion = useCallback(async(entityId, data, onSuccess, onFailure) => {
        setLoading(true);
        try {
            const response = await authPost(`/time-off/versions/${entityId}`, { data });
            if (response) {
                setData((prev) => (prev || []).concat(response));
                setTotalItems((prev) => Number(prev) + 1);
                onSuccess?.(response);
                toast.success(t("new-version-created-successfully"))
            }
        } catch(err) {
            onFailure?.(err);
        } finally {
            setLoading(false);
        }
    }, [authPost, t]);
    
    const updateVersion = useCallback(async(entityId, id, data) => {
        try {
            const response = await authPut(`/time-off/versions/${entityId}/${id}`, { data })
            if(response) {
                setData((prev) => (prev || []).map((item) => (
                    item.id === id ? response : item
                )))
            }
        } catch(err) {
            getErrorMessage(err, t)
        }
    }, [authPut, t]);

    const removeVersion = useCallback(async(id, entityId, onSuccess) => {
        try {
            await authDelete(`/time-off/versions/${entityId}/${id}`);
            setData((prev) => (prev || []).filter((item) => item.id !== id));
            setTotalItems((prev) => Number(prev || 1) -1);
            onSuccess?.(id);
        } catch(err) {
            getErrorMessage(err, t)
        }
    }, [authDelete, t]);

    return {
        data,
        loading,
        totalItems,
        getVersions,
        createVersion,
        updateVersion,
        removeVersion,
        getLatestVersion
    };
};
